'use client';

import { runWithViewTransition } from '@/lib/view-transition';
import { getCommandMenuSignOutAction } from '@/components/global-command-menu-actions';
import { useChangeAppLocale } from '@/hooks/use-change-app-locale';
import { api, useQuery } from '@/lib/convex';
import {
  Gauge,
  LogOut,
  Moon,
  Plus,
  Sun,
  Trash2,
  User,
  SquarePen,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import { startTransition, useEffect, useMemo, useState } from 'react';
import { buildChangeLanguageCommand } from '../language-command';
import type { CommandDef } from '../types';

export type QuickActionText = {
  lightMode: string;
  darkMode: string;
  createProduct: string;
  newThread: string;
  usage: string;
  trash: string;
  profileSettings: string;
  changeLanguage: string;
  searchLanguages: string;
  signOut: string;
};

export function useQuickActions({
  orgSlug,
  isAuthenticated,
  text,
  close,
}: {
  orgSlug?: string;
  isAuthenticated: boolean;
  text: QuickActionText;
  close: () => void;
}): CommandDef[] {
  const router = useRouter();
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch for theme
  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  const { data: creditStatus } = useQuery(
    api.credits.queries.getAvailableCreditsBySlug,
    orgSlug ? { orgSlug } : 'skip'
  );

  const { currentLocale, changeLocale } = useChangeAppLocale();

  const signOutAction = useMemo(
    () => getCommandMenuSignOutAction(isAuthenticated, text.signOut),
    [isAuthenticated, text.signOut]
  );

  return useMemo(() => {
    const actions: CommandDef[] = [];

    const themeAction: CommandDef = {
      id: 'toggle-theme',
      label:
        mounted && resolvedTheme === 'dark' ? text.lightMode : text.darkMode,
      icon: mounted && resolvedTheme === 'dark' ? Sun : Moon,
      run: () => {
        if (mounted) {
          const isDark = resolvedTheme === 'dark';
          // Use view transitions if supported
          runWithViewTransition(() => {
            setTheme(isDark ? 'light' : 'dark');
            close();
          });
        } else {
          close();
        }
      },
      keywords: ['theme', 'dark', 'light', 'mode', 'appearance', 'toggle'],
    };

    if (orgSlug) {
      actions.push({
        id: 'create-product',
        label: text.createProduct,
        icon: Plus,
        run: () => {
          router.push(`/${orgSlug}/products/new`);
          close();
        },
        keywords: ['create', 'new', 'product', 'add'],
      });
      actions.push({
        id: 'new-thread',
        label: text.newThread,
        icon: SquarePen,
        run: () => {
          router.push(`/${orgSlug}/design`);
          close();
        },
        keywords: ['create', 'new', 'thread', 'chat', 'conversation'],
      });
      actions.push(themeAction);
      const creditsBalance =
        creditStatus?.balance !== undefined
          ? Math.round(creditStatus.balance)
          : undefined;
      actions.push({
        id: 'usage',
        label: text.usage,
        icon: Gauge,
        run: () => {
          router.push(`/${orgSlug}/settings/usage`);
          close();
        },
        keywords: [
          'usage',
          'credits',
          'balance',
          'analytics',
          'stats',
          'statistics',
        ],
        badge: creditsBalance,
      });
      actions.push({
        id: 'trash',
        label: text.trash,
        icon: Trash2,
        run: () => {
          router.push(`/${orgSlug}/trash`);
          close();
        },
        keywords: ['trash', 'deleted', 'bin', 'remove'],
      });
    }

    actions.push({
      id: 'profile-settings',
      label: text.profileSettings,
      icon: User,
      run: () => {
        router.push('/settings/profile');
        close();
      },
      keywords: [
        'profile',
        'account',
        'user',
        'personal',
        'settings',
        'preferences',
      ],
    });

    if (isAuthenticated) {
      actions.push(
        buildChangeLanguageCommand({
          label: text.changeLanguage,
          placeholder: text.searchLanguages,
          currentLocale,
          onSelectLocale: changeLocale,
        })
      );
    }

    if (!orgSlug) {
      actions.push(themeAction);
    }

    if (signOutAction) {
      actions.push({
        id: signOutAction.id,
        label: signOutAction.label,
        icon: LogOut,
        run: () => {
          router.push(signOutAction.href);
          close();
        },
        keywords: signOutAction.keywords,
        destructive: true,
      });
    }

    return actions;
  }, [
    orgSlug,
    isAuthenticated,
    router,
    close,
    mounted,
    resolvedTheme,
    setTheme,
    text.createProduct,
    text.darkMode,
    text.lightMode,
    text.newThread,
    text.profileSettings,
    text.trash,
    text.usage,
    text.changeLanguage,
    text.searchLanguages,
    currentLocale,
    changeLocale,
    creditStatus,
    signOutAction,
  ]);
}

export function filterCommands(
  commands: CommandDef[],
  term: string
): CommandDef[] {
  if (!term) return commands;
  const lower = term.toLowerCase();
  return commands.filter(
    (command) =>
      command.keywords?.some((kw) => kw.includes(lower)) ||
      command.label.toLowerCase().includes(lower)
  );
}
